import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import { RotatingLines } from "react-loader-spinner";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import { useAuth } from "../../context/Auth";
import Loading from "../../components/Loading/Loading";
import { api_url } from "../../data";

const ChurchDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { requestWithAuth } = useAuth();
  
  const [churchData, setChurchData] = useState({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [available, setAvailable] = useState(true);
  const [passwordVisibility, setPasswordVisibility] = useState(false);
  const [originalUsername, setOriginalUsername] = useState("");
  const [isEditing, setIsEditing] = useState(false);

  // Fetch church details
  const fetchChurchDetails = async () => {
    setLoading(true);
    try {
      const responseData = await requestWithAuth(`/superadmin/churches/${id}`, 'GET');
      setChurchData(responseData?.data);
    } catch (error) {
      toast.error("Failed to fetch church details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (e) => {
    setChurchData({
      ...churchData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e) => {
    setChurchData({
      ...churchData,
      [e.target.name]: e.target.files[0]
    });
  };

  const submitForm = async (e) => {
    e.preventDefault();
    
    setUpdating(true);
    try {
      const formData = new FormData();
      Object.keys(churchData).forEach((key) => {
        if (churchData[key] !== null && churchData[key] !== undefined) {
          formData.append(key, churchData[key]);
        }
      });
      
      const responseData = await requestWithAuth(`/superadmin/churches/${id}/`, 'PUT', formData, {
        'Content-Type': 'multipart/form-data'
      });
      
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Church details updated successfully",
      });
      
      setIsEditing(false);
      fetchChurchDetails(); // Refresh the data
    } catch (error) {
      if (error.response?.data) {
        toast.error(error.response.data.message);
        if (error.response.status === 400) {
          for (const property in error.response.data.errors) {
            toast.error(`${property} error: ${error.response.data.errors[property]}`);
          }
        } else {
          toast.error("Internal server error");
        }
      } else {
        toast.error("Something went wrong!");
      }
    } finally {
      setUpdating(false);
    }
  };

  const deleteChurch = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await requestWithAuth(`/superadmin/churches/${id}`, 'DELETE');
        Swal.fire(
          'Deleted!',
          'Church has been deleted.',
          'success'
        ).then(() => {
          navigate('/super/churches');
        });
      } catch (error) {
        toast.error("Failed to delete church");
      }
    }
  };

  useEffect(() => {
    fetchChurchDetails();
  }, [id]);


  if (loading) {
    return (
      <Loading />
    );
  }

  return (
    <div>
      <Header title="Church Details" />
      
      {/* Action Buttons */}
      <div className="flex justify-between items-center mb-4 px-4">
        <button
          onClick={() => navigate('/super/churches')}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          ← Back to Churches
        </button>
        
        <div className="flex gap-2">
          {!isEditing ? (
            <button
              onClick={() => setIsEditing(true)}
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Edit Details
            </button>
          ) : (
            <button
              onClick={() => {
                setIsEditing(false);
                fetchChurchDetails(); // Reset to original data
              }}
              className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
            >
              Cancel Edit
            </button>
          )}
          
          <button
            onClick={deleteChurch}
            className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-600"
          >
            Delete Church
          </button>
        </div>
      </div>

      <form onSubmit={submitForm} className="border border-gray-500">
        <div className="bg-primary text-secondary px-5 py-2">
          CHURCH DETAILS
        </div>
        <div className="grid lg:grid-cols-3 gap-5 p-5">
          <div className="flex flex-col">
            <label htmlFor="church_logo">Church Logo</label>
            {!isEditing && churchData?.church_logo && (
              <div className="mb-2">
                <img 
                  src={`${api_url}${churchData.church_logo}`} 
                  alt="Church Logo" 
                  className="w-20 h-20 object-cover rounded"
                />
              </div>
            )}
            <input
              type="file"
              onChange={handleFileChange}
              name="church_logo"
              id="church_logo"
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="church_name">
              Church Name
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              onChange={handleChange}
              name="church_name"
              id="church_name"
              value={churchData.church_name || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
          </div>
          
          <div className="flex flex-col">
            <label htmlFor="church_branch">
              Church Branch
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              onChange={handleChange}
              name="church_branch"
              id="church_branch"
              value={churchData.church_branch || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
            <small>This can be the congregation name, parish name etc</small>
          </div>

          <div className="flex flex-col">
            <label htmlFor="church_phone_number">Phone Number</label>
            <input
              type="text"
              onChange={handleChange}
              name="church_phone_number"
              id="church_phone_number"
              value={churchData.church_phone_number || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="church_email">
              Email
              <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="church_email"
              id="church_email"
              onChange={handleChange}
              value={churchData.church_email || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="church_location">
              Location
              <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              onChange={handleChange}
              name="church_location"
              id="church_location"
              value={churchData.church_location || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="sms_sender_id">SMS Sender ID</label>
            <input
              type="text"
              onChange={handleChange}
              name="sms_sender_id"
              id="sms_sender_id"
              value={churchData.sms_sender_id || ''}
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
            <small>Default sender ID for SMS messages</small>
          </div>

          <div className="flex flex-col">
            <label htmlFor="sms_bundle">SMS Bundle</label>
            <input
              type="number"
              onChange={handleChange}
              name="sms_bundle"
              id="sms_bundle"
              value={churchData.sms_bundle || ''}
              min="0"
              disabled={!isEditing}
              className={`border border-gray-500 px-3 py-2 outline-none rounded ${!isEditing ? 'bg-gray-100' : ''}`}
            />
            <small>Number of SMS credits allocated</small>
          </div>
        </div>

        

        {isEditing && (
          <div className="p-5 flex items-center justify-end gap-3">
            {updating ? (
              <div className="flex gap-2 p-2 bg-gray-300 rounded">
                <RotatingLines strokeColor="black" height="20" width="20" />
                Updating Church...
              </div>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    fetchChurchDetails();
                  }}
                  className="bg-gray-500 px-4 py-2 text-white rounded-md cursor-pointer hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="bg-primary px-4 py-2 text-white rounded-md cursor-pointer hover:bg-primary/90"
                >
                  Update Church
                </button>
              </>
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default ChurchDetail;